import { ContentOutletComponent, HasSubscriptionsToClose } from '@agingplan';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { BreakpointWatcherService } from 'src/app/services/breakpoint-watcher.service';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    AOS: {
      init(): void;
    };
  }
}

/**
 * Home component that displays the main landing page with parallax effects
 * Handles responsive background images and scroll-based animations
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent extends HasSubscriptionsToClose() implements OnInit, AfterViewInit, OnDestroy {
  /** Reference to mobile header image for parallax effect */
  @ViewChild('mobileImg', { static: true }) mobileHeaderImg!: ElementRef<HTMLDivElement>;

  /** Reference to read more content outlet */
  @ViewChild('popover_read_more', { static: true }) popover_read_more!: ContentOutletComponent;

  /** Reference to main index header for background manipulation */
  @ViewChild('index_header', { static: true }) index_header!: ElementRef<HTMLDivElement>;

  /** Environment configuration */
  public readonly env = environment;

  /** Store ScrollTrigger instances for cleanup */
  private scrollTriggers: ScrollTrigger[] = [];

  constructor(private breakpointService: BreakpointWatcherService) {
    super();
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
  }

  ngOnInit(): void {
    // Initialize AOS library with type safety
    if (window.AOS) {
      window.AOS.init();
    }
  }

  ngAfterViewInit(): void {
    // Handle responsive background images based on breakpoint
    this.subscribeTo(
      this.breakpointService.watchBreakPoints().subscribe((breakpoint) => {
        switch (breakpoint) {
          case 'desktop':
            this.index_header.nativeElement.style.backgroundImage = `url(${this.env.google.bucket}/assets/home-page/agingplan/WebPage_Header_Background_Desktop_3840x2160.jpg)`;
            break;
          case 'tablet':
            this.index_header.nativeElement.style.backgroundImage = `url(${this.env.google.bucket}/assets/home-page/agingplan/WebPage_Header_Couple.jpg)`;
            break;
        }
      }),
    );

    this.initializeParallaxEffects();
  }

  /**
   * Clean up GSAP ScrollTrigger instances to prevent memory leaks
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.scrollTriggers.forEach((trigger) => trigger.kill());
  }

  /**
   * Initialize desktop and mobile parallax effects using GSAP
   */
  private initializeParallaxEffects(): void {
    const section = document.getElementById('homeHeader');
    if (!section) return;

    // Desktop background parallax
    const header = document.querySelector<HTMLDivElement>('.parallax__layer--base');
    if (header) {
      header.style.backgroundPosition = `50% ${-innerHeight / 2}px`;
      this.scrollTriggers.push(
        ScrollTrigger.create({
          trigger: section,
          animation: gsap.to(header, {
            backgroundPosition: `50% ${innerHeight / 2}px`,
            ease: 'none',
          }),
          scrub: true,
        }),
      );
    }

    // Desktop foreground couple parallax
    const people = document.querySelector<HTMLDivElement>('.parallax__layer--fore');
    if (people) {
      people.style.backgroundPosition = `50% ${-innerHeight / 4}px`;
      this.scrollTriggers.push(
        ScrollTrigger.create({
          trigger: section,
          animation: gsap.to(people, {
            backgroundPosition: `50% ${innerHeight / 4}px`,
            ease: 'none',
          }),
          scrub: true,
        }),
      );
    }

    // Mobile parallax
    const image = this.mobileHeaderImg.nativeElement;
    image.style.backgroundPosition = `11% ${-innerHeight / 2}px`;
    this.scrollTriggers.push(
      ScrollTrigger.create({
        trigger: section,
        animation: gsap.to(image, {
          backgroundPosition: `11% ${innerHeight / 2}px`,
          ease: 'none',
        }),
        scrub: true,
      }),
    );
  }

  /**
   * Opens the read more popover and scrolls to top of page
   */
  openReadMore(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.popover_read_more.open();
  }
}
